import React, { useEffect } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { CheckCircle2, Download, CalendarDays, ArrowLeft, ArrowRight, Phone } from "lucide-react";
import { brand, media, cta } from "../content/siteContent";
import { Footer } from "../components/landing/Footer";
import { Seo } from "../components/Seo";
import { CTAButton } from "../components/landing/primitives";
import { useConfig } from "../hooks/useConfig";
import { track, EVENTS } from "../lib/analytics";

const CONTENT = {
  prospectus: {
    eyebrow: "Your Prospectus is ready",
    heading: "Thank you — your 2026 Prospectus is ready.",
    body:
      "We've emailed you a copy and a member of our team will be in touch within 24 business hours. Download the prospectus below to explore the full transformation system, the simulation and the qualification pathway.",
    download: "prospectus",
  },
  sample: {
    eyebrow: "Your sample is ready",
    heading: "Thank you — your training sample is ready.",
    body:
      "We've emailed you a copy too. Download the Meeting Management guide below — it's the same practical, workplace-ready standard you'll find throughout the Operations Transformation Program.",
    download: "sample",
  },
  consultation: {
    eyebrow: "Request received",
    heading: "Thank you — your Pre-Training Review is requested.",
    body:
      "A member of our team will be in touch within 24 business hours to arrange your Pre-Training Review. You can book a time now, or download the prospectus while you wait.",
    download: "prospectus",
  },
  cohort: {
    eyebrow: "Enquiry received",
    heading: "Thank you — we've received your cohort enquiry.",
    body:
      "A member of our team will be in touch within 24 business hours to discuss cohort pricing and a delivery plan tailored to your leadership team. In the meantime, feel free to download the prospectus.",
    download: "prospectus",
  },
  default: {
    eyebrow: "Thank you",
    heading: "Thank you — your request is confirmed.",
    body:
      "We've sent a confirmation to your inbox and a member of our team will be in touch within 24 business hours.",
    download: "prospectus",
  },
};

export default function ThankYou() {
  const config = useConfig();
  const [params] = useSearchParams();
  const location = useLocation();
  const isSample = location.pathname.includes("free-sample");
  const type = isSample ? "sample" : params.get("type") || "prospectus";
  const content = CONTENT[type] || CONTENT.default;

  useEffect(() => {
    // Fire a distinct conversion event + SPA pageview for GA4 / Google Ads / Meta.
    track(EVENTS.LEAD_CONVERTED, { type, path: location.pathname });
    track(EVENTS.THANK_YOU_VIEW, { type, path: location.pathname });
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", { page_path: location.pathname, page_title: "Thank You" });
    }
    // PIXEL_PLACEHOLDER: drop Google Ads / Meta conversion snippet here (fires once per thank-you view).
  }, [type, location.pathname]);

  const downloadUrl = content.download === "sample" ? config.sample_material_url : config.prospectus_url;
  const downloadLabel =
    content.download === "sample" ? "Download the Meeting Management guide" : "Download the 2026 Prospectus";
  const downloadEvent =
    content.download === "sample" ? EVENTS.SAMPLE_DOWNLOADED : EVENTS.PROSPECTUS_DOWNLOADED;

  return (
    <div className="min-h-screen flex flex-col bg-navy">
      <Seo path={location.pathname} title="Thank you | Operations Transformation Program" noindex />
      {/* Compliance strip */}
      <div className="bg-[#081321] border-b border-hair-dark">
        <div className="container-page py-1.5">
          <p className="text-[11px] sm:text-xs leading-snug text-center sm:text-left">
            <span className="hidden sm:inline font-display font-semibold text-white">{brand.name}</span>
            <span className="hidden sm:inline text-white/50"> — </span>
            <span className="hidden sm:inline text-white/65">{brand.complianceLine}</span>
            <span className="sm:hidden text-white/70">Delivered &amp; assessed by Axiom College · RTO 40489</span>
          </p>
        </div>
      </div>

      {/* Slim brand bar */}
      <div className="container-page flex items-center justify-between h-[72px]">
        <Link to="/" className="flex items-center gap-3 focus-ring rounded-sm" data-testid="thankyou-home-logo">
          <img src={media.logoWhite} alt="Axiom College — RTO 40489" className="h-10 w-auto" />
        </Link>
        <div className="flex items-center gap-4 sm:gap-6">
          <a
            href={`tel:${brand.phone.replace(/\s+/g, "")}`}
            data-testid="thankyou-phone"
            onClick={() => track(EVENTS.PHONE_CALL, { location: "thank_you" })}
            className="inline-flex items-center gap-2 text-white hover:text-teal text-sm font-display font-semibold transition-colors duration-300"
          >
            <Phone className="h-4 w-4" /> {brand.phone}
          </a>
          <Link
            to="/"
            data-testid="thankyou-back"
            className="hidden sm:inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-display font-semibold focus-ring rounded-sm"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center relative overflow-hidden px-4 py-16">
        <div className="absolute inset-0 blueprint opacity-40" />
        <div
          className="relative w-full max-w-xl bg-offwhite border border-hair rounded-sm p-8 lg:p-12 text-center"
          data-testid="thankyou-card"
        >
          <CheckCircle2 className="h-14 w-14 text-teal mx-auto" />
          <p className="eyebrow text-teal mt-6">{content.eyebrow}</p>
          <h1 className="mt-3 font-display font-extrabold text-navy text-3xl lg:text-4xl tracking-tight leading-tight">
            {content.heading}
          </h1>
          <p className="mt-4 text-slate-ink text-sm lg:text-base leading-relaxed max-w-md mx-auto">
            {content.body}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            {downloadUrl && (
              <CTAButton
                as="a"
                href={downloadUrl}
                target="_blank"
                rel="noopener"
                data-testid="thankyou-download"
                onClick={() => track(downloadEvent, { location: "thank_you" })}
                withArrow
              >
                <Download className="h-4 w-4" /> {downloadLabel}
              </CTAButton>
            )}
            {config.calendar_url && (
              <CTAButton
                as="a"
                variant="outlineDark"
                href={config.calendar_url}
                target="_blank"
                rel="noopener"
                data-testid="thankyou-calendar"
                onClick={() => track(EVENTS.CALENDAR_OPENED, { location: "thank_you" })}
              >
                <CalendarDays className="h-4 w-4" /> {cta.consultation}
              </CTAButton>
            )}
          </div>

          <Link
            to="/"
            data-testid="thankyou-home-link"
            className="mt-8 inline-flex items-center gap-2 text-teal text-sm font-display font-semibold hover:underline focus-ring rounded-sm"
          >
            Return to homepage <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
